import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  IconButton
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from '../../commons/useTranslasion';
import { useSolicitacaoActions } from '../../states/solicitacoes/solicitacao.actions';
import { ISolicitacaoRequest } from '../../commons/types/solicitacoes';

const CriarSolicitacao: React.FC = () => {
  const { translate } = useTranslation();
  const history = useHistory();
  const solicitacaoActions = useSolicitacaoActions();
  const { register, handleSubmit, formState: { errors } } = useForm();
  
  const [anexos, setAnexos] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileList = Array.from(e.target.files);
      
      // Validar tamanho dos arquivos (máximo 10MB cada)
      const invalidFiles = fileList.filter(file => file.size > 10 * 1024 * 1024);
      if (invalidFiles.length > 0) {
        setSnackbarMessage(translate('tamanhoMaximoArquivo'));
        setSnackbarOpen(true);
        return;
      }
      
      // Validar quantidade de arquivos (máximo 5)
      if (anexos.length + fileList.length > 5) {
        setSnackbarMessage(translate('limiteArquivos'));
        setSnackbarOpen(true);
        return;
      }
      
      setAnexos(prev => [...prev, ...fileList]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setAnexos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const onSubmit = async (data: any) => {
    if (!data.titulo || !data.descricao) {
      setSubmitError(translate('preenchaCamposObrigatorios'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Obter IDs do usuário autenticado
      let assessoradoId: number;
      let comandaId: number;
      
      try {
        assessoradoId = solicitacaoActions.getAssessoradoId();
        comandaId = solicitacaoActions.getComandaId();
      } catch (error) {
        setSubmitError(typeof error === 'string' ? error : 'Não foi possível obter informações do usuário');
        setIsSubmitting(false);
        return;
      }
      
      const solicitacaoData: ISolicitacaoRequest = {
        titulo: data.titulo,
        descricao: data.descricao,
        assessoradoId,
        comandaId,
        anexos: anexos.length > 0 ? anexos : undefined
      };
      
      await solicitacaoActions.criarSolicitacao(solicitacaoData);
      
      setSubmitSuccess(true);
      setSnackbarMessage(translate('solicitacaoCriadaComSucesso'));
      setSnackbarOpen(true);
      
      // Redirecionar para a página de listagem após 2 segundos
      setTimeout(() => {
        history.push('/solicitacoes');
      }, 2000);
    } catch (error) {
      setSubmitError(typeof error === 'string' ? error : translate('erroAoCriarSolicitacao'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoltar = () => {
    history.push('/solicitacoes');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleVoltar} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {translate('criarSolicitacao')}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        {submitSuccess ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              {translate('solicitacaoCriadaComSucesso')}
            </Typography>
            <Typography>
              {translate('voltarParaListagem')}
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label={translate('tituloSolicitacao')}
                  fullWidth
                  variant="outlined"
                  {...register('titulo', { required: true })}
                  error={!!errors.titulo}
                  helperText={errors.titulo ? translate('preenchaCamposObrigatorios') : ''}
                  disabled={isSubmitting}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label={translate('descricaoSolicitacao')}
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  {...register('descricao', { required: true })}
                  error={!!errors.descricao}
                  helperText={errors.descricao ? translate('preenchaCamposObrigatorios') : ''}
                  disabled={isSubmitting}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    disabled={isSubmitting || anexos.length >= 5}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={isSubmitting || anexos.length >= 5}
                    >
                      {translate('anexarArquivos')}
                    </Button>
                  </label>
                  
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    {translate('tamanhoMaximoArquivo')} | {translate('limiteArquivos')}
                  </Typography>
                  
                  {anexos.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {translate('anexos')} ({anexos.length}/5):
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1 }}>
                        {anexos.map((file, index) => (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              p: 1,
                              borderBottom: index < anexos.length - 1 ? '1px solid #eee' : 'none'
                            }}
                          >
                            <Typography variant="body2" noWrap sx={{ maxWidth: '80%' }}>
                              {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveFile(index)}
                              disabled={isSubmitting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isSubmitting ? translate('enviando') : translate('enviar')}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
      </Box>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default CriarSolicitacao;
